import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo, useJsonLd } from "@/lib/seo";
import { ArrowUpRight } from "lucide-react";

export default function Journal() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/articles");
        if (!cancelled) setArticles(data || []);
      } catch {}
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useSeo({
    title: "Journal · BARIQUE",
    description: "Stories from the atelier — craft, colour, and couture from BARIQUE, New Delhi.",
    canonical: `${origin}/journal`,
    image: articles[0]?.cover,
    type: "website",
  });
  useJsonLd([{
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "BARIQUE Journal",
    url: `${origin}/journal`,
    ...(articles.length ? {
      blogPost: articles.slice(0, 20).map((a) => ({
        "@type": "BlogPosting",
        headline: a.title,
        url: `${origin}/journal/${a.slug}`,
        datePublished: a.published_at,
        author: { "@type": "Organization", name: a.author || "BARIQUE" },
        image: a.cover,
        articleBody: (a.excerpt || "").slice(0, 400),
      })),
    } : {}),
  }]);

  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="journal-page">
      <div className="container-lux">
        <p className="overline text-muted-foreground">The Editorial</p>
        <h1 className="mt-4 font-serif tracking-tighter text-5xl md:text-7xl font-light">Journal</h1>
        <p className="mt-6 max-w-2xl text-sm text-muted-foreground">
          Stories from our atelier in New Delhi — the craft, the colour, and the couture worth waiting for.
        </p>
        <div className="divider-thin my-10" />

        {loading && (<p className="overline text-muted-foreground/60">Loading…</p>)}
        {!loading && articles.length === 0 && (
          <p className="text-center py-24 text-muted-foreground">No stories yet — come back soon.</p>
        )}

        <div className="grid md:grid-cols-2 gap-10 md:gap-14">
          {articles.map((a, i) => (
            <Link
              key={a.id || a.slug}
              to={`/journal/${a.slug}`}
              data-testid={`journal-card-${i}`}
              className="group block"
            >
              <div className="aspect-[4/5] overflow-hidden bg-muted">
                {a.cover ? (
                  <img
                    src={a.cover}
                    loading="lazy"
                    alt={a.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : null}
              </div>
              <div className="mt-6">
                <p className="overline text-muted-foreground">
                  {(a.tags || []).slice(0, 2).join(" · ")}
                  {a.read_minutes ? ` · ${a.read_minutes} min read` : ""}
                </p>
                <h2 className="mt-3 font-serif text-2xl md:text-3xl leading-tight tracking-tight">{a.title}</h2>
                <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{a.excerpt}</p>
                <span className="mt-5 inline-flex items-center gap-2 overline group-hover:opacity-70 transition-opacity">
                  Read the story <ArrowUpRight size={14} strokeWidth={1.25} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
